import React from 'react';
import { Modal } from '../forms/AdminForm';
import { Select, ReadOnlyField, FormSection } from '../forms/FormComponents';
import { useUserProfile, useDepartmentOffice } from '../../hooks';
import { User } from '../../types/auth';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userInfo: User | null;
  setUserInfo: React.Dispatch<React.SetStateAction<User | null>>;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, userInfo, setUserInfo }) => {
  const { profileForm, setProfileForm, handleProfileSave } = useUserProfile(userInfo, isOpen);

  const { departments, offices } = useDepartmentOffice(
    isOpen,
    profileForm.management,
    profileForm.office,
    setProfileForm
  );

  const onSave = () => {
    handleProfileSave(setUserInfo, () => onClose());
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Profil Ayarları"
      onSave={onSave}
      saveText="Kaydet"
    >
      <div className="space-y-6">
        {/* Kişisel Bilgiler Bölümü */}
        <FormSection
          title="Kişisel Bilgiler"
          icon={<div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ReadOnlyField label="Ad" value={profileForm.name} />
            <ReadOnlyField label="Soyad" value={profileForm.surname} />
            <ReadOnlyField label="Telefon Numarası" value={profileForm.phone_number} />
            <ReadOnlyField label="TC Kimlik No" value={profileForm.identification_number} />
          </div>
        </FormSection>

        {/* Yetki Bilgisi */}
        <FormSection
          title="Yetki Seviyesi"
          icon={<div className="w-2 h-2 bg-purple-500 rounded-full"></div>}
          className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-100"
        >
          <div className="flex items-center gap-3">
            <div
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                profileForm.authorization_level === 'Admin'
                  ? 'bg-purple-100 text-purple-800 border border-purple-200'
                  : 'bg-blue-100 text-blue-800 border border-blue-200'
              }`}
            >
              {profileForm.authorization_level === 'Admin' ? '👑 Admin' : '👤 Kullanıcı'}
            </div>
          </div>
        </FormSection>

        {/* Çalışma Bilgileri */}
        <FormSection
          title="Çalışma Bilgileri"
          icon={<div className="w-2 h-2 bg-green-500 rounded-full"></div>}
          className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-100"
        >
          <Select
            label="Departman"
            value={profileForm.management}
            onChange={value =>
              setProfileForm(f => ({
                ...f,
                management: value,
                office: '',
              }))
            }
            options={departments.map(d => ({
              value: d.name,
              label: d.name,
            }))}
          />
          <Select
            label="Büro"
            value={profileForm.office}
            onChange={value => setProfileForm(f => ({ ...f, office: value }))}
            options={offices.map(o => ({ value: o.name, label: o.name }))}
            disabled={!profileForm.management}
          />
        </FormSection>
      </div>
    </Modal>
  );
};

export default ProfileModal;
